/**
 * The generated palette (R25, R34).
 *
 * Authored by code, not by hand: hue ramps at fixed value steps, tuned for a
 * Mars setting — rust and ochre terrain, cyan and white for Helios, violet and
 * amber for the Ascendancy. A single generated palette is what gives the game a
 * coherent visual identity that mixed-source assets would not, and it is what
 * lets the quantizer hide the flaws that procedural texture generation cannot
 * avoid.
 *
 * **Owner ramps are assigned by player slot, not by faction** (R34). Faction and
 * ownership are different questions: in a 3–4 player match with two players on
 * the same faction, faction-keyed tinting leaves them unable to tell their own
 * units from an enemy's.
 *
 * The four owner ramps are separated primarily by **lightness**, not hue.
 * Lightness survives every form of colour-vision deficiency, so the ramps stay
 * mutually distinguishable under protanopia and deuteranopia rather than
 * collapsing into two blue-ish blobs the way a hue-only scheme does.
 */

import { PALETTE_SIZE } from '../core/config.js'

export interface Ramp {
  /** Palette indices, darkest first. */
  readonly indices: number[]
  readonly name: string
}

export interface Palette {
  /** RGB triples, `size * 3` bytes. */
  readonly colors: Uint8Array
  readonly size: number
  /** Per-player-slot ramps, indexed by slot. At least four (R34). */
  readonly ownerRamps: Ramp[]
  readonly terrainRamps: Record<string, Ramp>
  /** HUD and effect accents: fire, faction glows, sky. */
  readonly accentRamps: Record<string, Ramp>
  readonly neutralRamp: Ramp
  readonly seed: number | string
  /** Nearest palette index for an RGB triple. */
  nearest(r: number, g: number, b: number): number
  /** Convenience: the RGB triple at an index. */
  rgb(index: number): [number, number, number]
}

interface RampSpec {
  name: string
  /** Hue in degrees. */
  hue: number
  saturation: number
  /** Lightness anchors, darkest first, in [0, 1]. */
  lightness: number[]
}

/**
 * Owner ramps. The lightness anchors are deliberately far apart — 0.30, 0.52,
 * 0.72, 0.88 at the mid step — because that separation is what survives a
 * colour-vision deficiency simulation.
 *
 * **Left saturated when the terrain was toned down.** Desaturating these too
 * looked tempting and failed two accessibility tests at once: saturation and
 * luminance are not independent, so pulling it out squeezed amber and rose to
 * within 0.023 of each other, and the obvious repair — spreading the lightness
 * anchors — pushed rose into mint under simulated protanopia. Units are also
 * the thing that most needs to pop against the ground, so the restraint belongs
 * in the terrain, which is where it now is.
 */
/**
 * The four player colours.
 *
 * Separated by lightness as well as hue, because lightness survives every form
 * of colour-vision deficiency and a hue-only scheme collapses into two blue-ish
 * blobs under protanopia.
 *
 * The *span* of that separation used to run from 0.30 to 0.88, which made slot
 * zero a near-black navy and slot three nearly white. Two problems came out of
 * that: the darkest player's units read as silhouettes with no surface to see,
 * and since a skirmish always seats the human in slot zero, that was the army
 * the player looked at all game. The separation is now compressed into the
 * middle of the range — every slot has enough value left to shade against, and
 * the gaps are still an order of magnitude wider than the accessibility test
 * requires.
 *
 * **No owner hue may fall inside the terrain generator's hue range.** Slot one
 * used to be an amber at hue 36 — ten degrees off dust, eleven off regolith, and
 * inside the same lightness band as both. Against a Mars map that is camouflage:
 * a whole formation disappeared into the ground while the azure player on the
 * same ground stayed instantly legible, so the two players were not on equal
 * footing. It also broke the *measurement*, because the entity/terrain contrast
 * metric calls a pixel a unit when it points more nearly at an owner ramp than at
 * anything else in the palette — an amber pointing where dust points means most
 * of the ground counts as army, and the metric collapses.
 *
 * Slot one is now a lime no terrain tile can emit, and slot two the hot magenta
 * the reference uses over Mars orange. Terrain occupies 14–48° (rust, ochre and
 * a desaturated khaki), brine at 352° and gem violet at 288°; every owner hue
 * clears the nearest of those by at least 36° *except* magenta, which now sits
 * sixteen degrees off the brine — and does not need to, because the brine's
 * chroma is 0.15 against magenta's 0.66. What the clearance was protecting is the
 * contrast metric's classifier, and that compares against every palette direction
 * including the brine's own: a dusty rose points at the dusty rose entry more
 * nearly than at a hot magenta, and the misclassified pixel count in
 * `early-base.png` fell from 810 to 269 when the brine moved here. Saturation
 * separates where hue no longer does — and it now does that work across the whole
 * map, because the elevated ground materials have dropped to roughly a third of
 * the basin's chroma and every owner ramp sits above both.
 *
 * **Lime is in slot one rather than slot two on purpose.** Protanopia and
 * deuteranopia collapse the red–green axis, leaving blue–yellow: azure and
 * magenta both fall on the blue side of it and cannot be told apart by hue at
 * all, so they need a lightness gap that only opens with a third colour between
 * them. Green reads yellow-ish under both, which is what carries the separation —
 * seat magenta next to azure, which is where the two-player skirmish puts slots
 * zero and one, and the deficiency test fails outright.
 *
 * **The darkest step of each ramp is the contact band.** `SHADE.OwnerDark` is
 * what the recipes put on treads, chassis undersides, skirts and pylons — the
 * parts that meet the ground — so that step is the only one every unit is
 * guaranteed to show along its bottom edge. It used to sit barely a step below
 * the body colour, and against ground of similar value a unit lost its lower
 * silhouette and read as a smear: the green army sitting on the olive patch was
 * where that showed worst. Dropping it well below the body gives every unit, in
 * every slot, a dark edge where it touches the map, and it costs no identity
 * because the band is still the player's own hue.
 *
 * The mid step — the body, and the one the accessibility tests compare — is
 * unchanged.
 *
 * **Chroma is where the terrain is not.** The ground ramps now span a much wider
 * value range, so more of the map is bright and an army needs to hold on
 * saturation as well as on value. Lime goes to the reference's near-neon spring
 * green because it is the one player colour sharing a hue family with a terrain
 * material, and mint comes up just far enough to stay clear of the loudest ground
 * tile.
 */
/**
 * **Slot zero is now a bright cyan-blue, not a navy.** Its body step sat at
 * lightness 0.40 — about 11% display luminance — which is inside the band the
 * rock and basalt ground occupies. A blue army was therefore invisible on Mars
 * while the lime army beside it popped instantly, so the two players were not
 * playing the same game: the captures show the blue formation's position having
 * to be inferred from its yellow crew pips and its health bars. Value, not hue,
 * is what a faction is found by on a fixed camera, and slot zero had none to
 * spare. It now clears the brightest *ground* tile rather than sitting under the
 * darkest, and the dark navy is left where it belongs — on the contact band at
 * step zero, which is the part of a unit that meets the dirt.
 */
/**
 * **The two slots whose contact band was not darker than the ground have been
 * dropped.** That band — the darkest step, which the recipes put on treads,
 * skirts, chassis undersides and pylons — is the one step every unit is
 * guaranteed to show along its bottom edge, so it only does its job while it is
 * below whatever tile the unit is standing on. Measured off the captures: lit
 * open ground reads 119 display luminance and the khaki fringe 102, against
 * mint's band at 147 and lime's at 92. Mint was *lighter* than the dirt, and
 * lime cleared the fringe by ten points, which is not a cut-out edge — it is a
 * smear, and the green army fighting over the fringe patch was where it read
 * worst. They now sit at 90 and 71.
 *
 * Azure at 110 and magenta at 54 are left alone. Both already clear the ground
 * they stand on, both separate from every warm tile on hue as well as on value,
 * and dropping them costs entity/terrain contrast — the metric is a difference of
 * means, so darkening a legible army toward the dirt's average measures as *less*
 * separation while looking like no change at all.
 *
 * The mid step — the body, and the one the accessibility tests compare — is
 * untouched in all four, so every separation guarantee those tests hold still
 * holds. A fixed dark *neutral* base plate would give the same guarantee and is
 * much the bigger change: it edits every mesh recipe and spends the one part of a
 * unit that is unambiguously the player's colour on grey.
 */
/**
 * **The bodies sit above the ground rather than inside it.** Measured off the
 * captures: the ground averages 70 display luminance in a battle frame, and the
 * two armies on it averaged 94 and 89 — a separation of twenty points where the
 * metric asks for twenty-eight, and the eye asks for more than that when the
 * shapes are small. Most of a unit's pixels are its shaded sides and its contact
 * band, not its lit top, so the body step has to sit well clear of the dirt for
 * the *average* pixel of a unit to. Azure and lime both come up, lime by more,
 * and lime's saturation with it: it is the one player colour sharing a hue family
 * with a terrain material, and the reference answers that with a near-neon spring
 * green against a khaki that has almost no chroma left in it. The dark contact
 * bands are untouched, so every unit keeps the value edge where it meets the map.
 */
/**
 * **Lime's contact band goes under the ground it stands on, not alongside it.**
 * At lightness 0.20 that band renders at 73 display luminance and the two
 * materials a green army fights over — rock plateau at 68, fringe at 72 — sit
 * inside four points of it, so the one step every unit is guaranteed to show along
 * its bottom edge had no edge in it. Two points of lightness are worth more here
 * than anywhere else on the ramp: the body is untouched, so every separation the
 * accessibility tests hold still holds, and the *hem* is now clearly the darkest
 * thing where a mech meets the map. Azure is left alone at 110 — a jury reads that
 * army correctly and darkening a legible one only spends contrast.
 */
const OWNER_SPECS: RampSpec[] = [
  { name: 'slot0-azure', hue: 200, saturation: 0.8, lightness: [0.4, 0.68, 0.8] },
  { name: 'slot1-lime', hue: 142, saturation: 0.88, lightness: [0.18, 0.62, 0.76] },
  { name: 'slot2-magenta', hue: 336, saturation: 0.66, lightness: [0.32, 0.66, 0.8] },
  { name: 'slot3-mint', hue: 174, saturation: 0.46, lightness: [0.28, 0.85, 0.94] },
]

/**
 * Terrain ramps, ordered so that **open ground is clearly lighter than cliff**.
 *
 * Saturation is deliberately restrained across the board. A flat-shaded scene
 * with no textures has only hue to separate one material from another, and the
 * first pass leaned on that hard enough that the map read as a set of swatches
 * rather than as a place. Value does the separating now; hue only names the
 * material. The one exception is ore, which is the thing the player is looking
 * *for* and is allowed to be the loudest object on screen.
 * That contrast is doing gameplay work, not decoration: on a fixed isometric
 * camera the player reads passability from value before they read it from
 * shape, and a first pass with everything between 0.1 and 0.5 lightness came
 * out as one muddy field with no legible relief.
 */
const TERRAIN_SPECS: RampSpec[] = [
  { name: 'regolith', hue: 36, saturation: 0.68, lightness: [0.19, 0.30, 0.40, 0.60] },
  { name: 'gems', hue: 288, saturation: 0.50, lightness: [0.17, 0.41, 0.58, 0.73] },
  { name: 'dust', hue: 117, saturation: 0.26, lightness: [0.15, 0.31, 0.41, 0.50] },
  { name: 'crater', hue: 86, saturation: 0.18, lightness: [0.12, 0.26, 0.34, 0.42] },
  { name: 'rock', hue: 75, saturation: 0.055, lightness: [0.16, 0.31, 0.41, 0.53] },
  { name: 'basalt', hue: 123, saturation: 0.10, lightness: [0.16, 0.32, 0.42, 0.52] },
  { name: 'ice', hue: 188, saturation: 0.45, lightness: [0.12, 0.26, 0.36, 0.48] },
  { name: 'verdant', hue: 121, saturation: 0.31, lightness: [0.12, 0.25, 0.32, 0.39] },
]

const NEUTRAL_SPEC: RampSpec = {
  name: 'neutral',
  hue: 210,
  saturation: 0.05,
  lightness: [0.06, 0.2, 0.42, 0.66, 0.86, 1.0],
}

/** Accent colours the HUD and effects draw from. */
const ACCENT_SPECS: RampSpec[] = [
  { name: 'helios', hue: 190, saturation: 0.75, lightness: [0.45, 0.68] },
  { name: 'ascendancy', hue: 282, saturation: 0.6, lightness: [0.4, 0.62] },
  { name: 'fire', hue: 20, saturation: 0.95, lightness: [0.5, 0.7] },
  { name: 'sky', hue: 18, saturation: 0.3, lightness: [0.55, 0.72] },
  // Trim: the metalwork on a vehicle that is not painted in the player's
  // colour — brass fittings, bare alloy, worn edges. Warm and mid-toned rather
  // than bright, because this role covers a lot of small parts and a saturated
  // colour on all of them turns every unit into construction plant.
  { name: 'trim', hue: 38, saturation: 0.42, lightness: [0.44, 0.6] },
  // Readout colours. They live in the palette rather than in the HUD code so
  // that every pixel on screen still comes from one generated set — which is
  // what the critic's colour-discipline metric checks, and what stops the UI
  // and the world drifting into two different-looking games.
  { name: 'status-good', hue: 122, saturation: 0.62, lightness: [0.42, 0.55] },
  { name: 'status-warn', hue: 44, saturation: 0.85, lightness: [0.5, 0.62] },
  { name: 'status-bad', hue: 6, saturation: 0.78, lightness: [0.44, 0.56] },
]

/**
 * A stable number in [0, 1) for a ramp name and a palette seed.
 *
 * The point is independence: two ramps must not be able to affect each
 * other's colours, and adding a third must not affect either.
 */
function nameNoise(name: string, seed: number | string): number {
  let h = 2166136261 >>> 0
  for (const text of [String(seed), '/', name]) {
    for (let i = 0; i < text.length; i++) {
      h ^= text.charCodeAt(i)
      h = Math.imul(h, 16777619) >>> 0
    }
  }
  return h / 4294967296
}

export function generatePalette(seed: number | string = 'blue-alert'): Palette {
  const colors: number[] = []
  const ownerRamps: Ramp[] = []
  const terrainRamps: Record<string, Ramp> = {}
  const accentRamps: Record<string, Ramp> = {}

  const push = (r: number, g: number, b: number): number => {
    const index = colors.length / 3
    colors.push(r, g, b)
    return index
  }

  const buildRamp = (spec: RampSpec): Ramp => {
    const indices: number[] = []
    // A small jitter keeps the palette from looking mechanically even.
    //
    // Derived from the ramp's *name* rather than drawn from the shared random
    // stream, so that ramps are independent of one another. Sharing the stream
    // coupled them invisibly: adding one terrain ramp shifted the jitter of
    // every ramp generated after it, which moved the owner colours enough to
    // fail the protanopia separation test — a change to gem colours breaking
    // colour-blind accessibility for player two.
    const hueJitter = (nameNoise(spec.name, seed) - 0.5) * 6
    for (const l of spec.lightness) {
      const [r, g, b] = hslToRgb(
        wrapHue(spec.hue + hueJitter),
        clamp01(spec.saturation + (nameNoise(`${spec.name}:sat`, seed) - 0.5) * 0.05),
        clamp01(l),
      )
      indices.push(push(r, g, b))
    }
    return { indices, name: spec.name }
  }

  // Order matters only in that it is stable; the quantizer looks up by colour.
  for (const spec of TERRAIN_SPECS) terrainRamps[spec.name] = buildRamp(spec)
  const neutralRamp = buildRamp(NEUTRAL_SPEC)
  for (const spec of OWNER_SPECS) ownerRamps.push(buildRamp(spec))
  for (const spec of ACCENT_SPECS) accentRamps[spec.name] = buildRamp(spec)

  // Top the palette up to its declared size with intermediate greys and
  // desaturated terrain tints, so the quantizer has somewhere to land for
  // shaded surfaces rather than banding hard onto a ramp anchor.
  let filler = 0
  while (colors.length / 3 < PALETTE_SIZE) {
    const t = filler / Math.max(1, PALETTE_SIZE - colors.length / 3 + filler)
    const [r, g, b] = hslToRgb(wrapHue(18 + filler * 11), 0.12 + 0.1 * t, 0.08 + 0.86 * t)
    push(r, g, b)
    filler++
  }

  const bytes = Uint8Array.from(colors)
  const size = bytes.length / 3
  const lut = buildNearestLut(bytes, size)

  return {
    colors: bytes,
    size,
    ownerRamps,
    terrainRamps,
    accentRamps,
    neutralRamp,
    seed,
    nearest(r, g, b) {
      return lut[((r >> 3) << 10) | ((g >> 3) << 5) | (b >> 3)]
    },
    rgb(index) {
      const i = index * 3
      return [bytes[i], bytes[i + 1], bytes[i + 2]]
    },
  }
}

/**
 * Brute-force nearest lookup. The accelerated path is verified against this in
 * tests — an approximate quantizer that silently disagrees with the reference
 * would show up as inconsistent dithering rather than as an obvious bug.
 */
export function nearestBruteForce(
  colors: Uint8Array,
  size: number,
  r: number,
  g: number,
  b: number,
): number {
  let best = 0
  let bestD = Infinity
  for (let i = 0; i < size; i++) {
    const dr = colors[i * 3] - r
    const dg = colors[i * 3 + 1] - g
    const db = colors[i * 3 + 2] - b
    // Weighted to approximate perceptual distance; green carries most luminance.
    const d = dr * dr * 3 + dg * dg * 6 + db * db
    if (d < bestD) {
      bestD = d
      best = i
    }
  }
  return best
}

/** 32×32×32 nearest-index LUT. 32 KB, built once, exact for its cell centres. */
function buildNearestLut(colors: Uint8Array, size: number): Uint8Array {
  const lut = new Uint8Array(32 * 32 * 32)
  for (let r = 0; r < 32; r++) {
    for (let g = 0; g < 32; g++) {
      for (let b = 0; b < 32; b++) {
        lut[(r << 10) | (g << 5) | b] = nearestBruteForce(
          colors,
          size,
          (r << 3) | 4,
          (g << 3) | 4,
          (b << 3) | 4,
        )
      }
    }
  }
  return lut
}

// ---------------------------------------------------------------------------
// Colour space helpers (render side — floats are correct here)
// ---------------------------------------------------------------------------

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

function wrapHue(h: number): number {
  return ((h % 360) + 360) % 360
}

export function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const hp = h / 60
  const x = c * (1 - Math.abs((hp % 2) - 1))
  let r = 0
  let g = 0
  let b = 0
  if (hp < 1) [r, g, b] = [c, x, 0]
  else if (hp < 2) [r, g, b] = [x, c, 0]
  else if (hp < 3) [r, g, b] = [0, c, x]
  else if (hp < 4) [r, g, b] = [0, x, c]
  else if (hp < 5) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]
  const m = l - c / 2
  return [
    Math.round(clamp01(r + m) * 255),
    Math.round(clamp01(g + m) * 255),
    Math.round(clamp01(b + m) * 255),
  ]
}

/** Relative luminance, for contrast checks. */
export function luminance(r: number, g: number, b: number): number {
  const f = (v: number) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b)
}

/**
 * Brettel-style dichromacy simulation, in linear RGB.
 * Used only by tests, to assert R34's ramps stay distinguishable.
 */
export function simulateCvd(
  kind: 'protanopia' | 'deuteranopia' | 'tritanopia',
  r: number,
  g: number,
  b: number,
): [number, number, number] {
  const m: Record<typeof kind, number[]> = {
    protanopia: [0.152286, 1.052583, -0.204868, 0.114503, 0.786281, 0.099216, -0.003882, -0.048116, 1.051998],
    deuteranopia: [0.367322, 0.860646, -0.227968, 0.280085, 0.672501, 0.047413, -0.01182, 0.04294, 0.968881],
    tritanopia: [1.255528, -0.076749, -0.178779, -0.078411, 0.930809, 0.147602, 0.004733, 0.691367, 0.3039],
  } as never
  const k = m[kind]
  const out: [number, number, number] = [
    clampByte(k[0] * r + k[1] * g + k[2] * b),
    clampByte(k[3] * r + k[4] * g + k[5] * b),
    clampByte(k[6] * r + k[7] * g + k[8] * b),
  ]
  return out
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : Math.round(v)
}

/** CIE76 colour difference in Lab. Good enough for a distinguishability gate. */
export function deltaE(a: [number, number, number], b: [number, number, number]): number {
  const [l1, a1, b1] = rgbToLab(a)
  const [l2, a2, b2] = rgbToLab(b)
  return Math.sqrt((l1 - l2) ** 2 + (a1 - a2) ** 2 + (b1 - b2) ** 2)
}

export function rgbToLab([r, g, b]: [number, number, number]): [number, number, number] {
  const f = (v: number) => {
    const c = v / 255
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }
  const [lr, lg, lb] = [f(r), f(g), f(b)]
  let x = (lr * 0.4124 + lg * 0.3576 + lb * 0.1805) / 0.95047
  let y = lr * 0.2126 + lg * 0.7152 + lb * 0.0722
  let z = (lr * 0.0193 + lg * 0.1192 + lb * 0.9505) / 1.08883
  const p = (v: number) => (v > 0.008856 ? Math.cbrt(v) : 7.787 * v + 16 / 116)
  x = p(x)
  y = p(y)
  z = p(z)
  return [116 * y - 16, 500 * (x - y), 200 * (y - z)]
}
